import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

export default function RegisterCompanyPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passordene matcher ikke');
      setIsLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      setError('Passordet må være minst 8 tegn');
      setIsLoading(false);
      return;
    }

    try {
      // TODO: Replace with actual registration API call
      // For now, simulate registration and auto-login
      console.log('Registering employer:', formData);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Auto-login after successful registration
      const success = await login(formData.email, formData.password);

      if (success) {
        navigate('/');
      } else {
        setError('Kunne ikke logge inn. Prøv å logge inn manuelt.');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError('Noe gikk galt. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="w-full max-w-md p-8 space-y-6">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/logo/frame.png"
            alt="Findr Logo"
            width={120}
            height={120}
          />
        </div>

        {/* Title */}
        <div className="text-center space-y-2">
          <h1
            className="font-bold"
            style={{
              fontSize: 'var(--font-size-title-lg)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)'
            }}
          >
            Opprett arbeidsgiver-konto
          </h1>
          <p style={{ fontSize: 'var(--font-size-body-md)', color: 'var(--color-text-secondary)' }}>
            Finn de beste kandidatene
          </p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium mb-2">
                Fornavn
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium mb-2">
                Etternavn
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              />
            </div>
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium mb-2">
              Bedriftsnavn
            </label>
            <input
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              E-postadresse
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2">
              Passord
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Minimum 8 tegn
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
              Bekreft passord
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Oppretter konto...' : 'Opprett konto'}
          </Button>
        </form>

        {/* Back to Login */}
        <div className="text-center pt-4">
          <button
            type="button"
            onClick={() => navigate('/auth')}
            className="text-sm transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Har du allerede konto?{' '}
            <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-weight-semibold)' }}>
              Logg inn
            </span>
          </button>
          <span style={{ color: 'var(--color-text-secondary)', margin: '0 8px' }}>•</span>
          <button
            type="button"
            onClick={async () => {
              setIsLoading(true);
              const success = await login('employer@testdomene.no', 'Admin123');
              if (success) {
                navigate('/');
              }
              setIsLoading(false);
            }}
            className="text-sm transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            disabled={isLoading}
          >
            <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-weight-semibold)' }}>
              Skipp
            </span>
          </button>
        </div>

        {/* Terms and Privacy */}
        <div className="text-center pt-4">
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Ved å logge deg inn eller registrere deg godtar du våre{' '}
            <a href="/terms" className="underline" style={{ color: 'var(--color-text-primary)' }}>
              Brukervilkår
            </a>
            {' '}og{' '}
            <a href="/privacy" className="underline" style={{ color: 'var(--color-text-primary)' }}>
              Personvernerklæring
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

